import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authJsonHeaders } from '../utils/authHeaders';
import { APP_MODULES, ALL_MODULE_IDS, normalizeModules } from '../utils/appModules';
import CabinetsAdminShell from './CabinetsAdminShell';
import './CabinetsSelectPage.css';
import './AdminCabinetsClientsPage.css';
import './AdminPointAccesPage.css';

function userDisplayName(u) {
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ');
  return full || u.username || `User #${u.id}`;
}

function userRoleLabel(u) {
  const a = String(u.access_level || u.role || '');
  if (a === 'Admin' || a === 'SuperAdmin' || a === 'admin') return 'Admin système';
  return 'Caissier';
}

/** Liste cabinets en cartes (même front + clic bordure que Gérer cabinet) */
function CabinetsListView({ cabinets, loading, error, memberCounts, onManage }) {
  return (
    <>
      {error ? <div className="cabinets-alert">{error}</div> : null}

      {loading ? (
        <div className="cabinets-loading">Chargement des cabinets…</div>
      ) : cabinets.length === 0 ? (
        <div className="cabinets-empty">Aucun cabinet trouvé.</div>
      ) : (
        <div className="cabinets-grid">
          {cabinets.map((cab) => {
            const isDefault = Number(cab.is_default) === 1;
            return (
              <article
                key={cab.id}
                className={`cabinet-card${isDefault ? ' cabinet-card--default' : ''}`}
                role="button"
                tabIndex={0}
                aria-label={`Ouvrir le point d'accès ${cab.name || ''}`}
                onClick={() => onManage(cab)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onManage(cab);
                  }
                }}
              >
                <div className="cabinet-card__glow" aria-hidden="true" />
                <div className="cabinet-card__top">
                  <div
                    className={`cabinet-card__badge${
                      isDefault ? ' cabinet-card__badge--live' : ' cabinet-card__badge--new'
                    }`}
                  >
                    {isDefault ? 'Données actuelles' : 'Nouveau / vide'}
                  </div>
                </div>
                <div className="cabinet-card__icon-wrap" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
                  </svg>
                </div>
                <h2>{cab.name}</h2>
                <p className="cabinet-card__code">{cab.code || '—'}</p>
                <p className="cabinet-card__role">
                  <span className="cabinet-card__role-pill">
                    {memberCounts[cab.id] != null
                      ? `${memberCounts[cab.id]} utilisateur${memberCounts[cab.id] !== 1 ? 's' : ''}`
                      : '…'}
                  </span>
                </p>
                <div className="cabinet-card__actions">
                  <button
                    type="button"
                    className="cabinets-btn cabinets-btn--primary cabinet-card__enter"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      onManage(cab);
                    }}
                  >
                    Point d&apos;accès
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * Point d'accès d'UN cabinet :
 * - coche = membre de CE cabinet seulement
 * - même user coché sur cabinet 1 et 2 = accès aux deux
 */
function CabinetMembersView({ cabinet, onBack, onSaved }) {
  const [allUsers, setAllUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [rolesByUser, setRolesByUser] = useState(() => ({}));
  const [modulesByUser, setModulesByUser] = useState(() => ({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');

  const applyMembersToState = (members) => {
    const ids = new Set();
    const roles = {};
    const mods = {};
    (Array.isArray(members) ? members : []).forEach((m) => {
      const uid = Number(m.user_id);
      if (!Number.isFinite(uid)) return;
      ids.add(uid);
      roles[uid] = String(m.member_role) === 'Admin' ? 'Admin' : 'User';
      mods[uid] = normalizeModules(m.allowed_modules, { defaultAll: false });
    });
    setSelectedIds(ids);
    setRolesByUser(roles);
    setModulesByUser(mods);
  };

  useEffect(() => {
    if (!cabinet?.id) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setSuccess('');
      try {
        const [usersRes, membersRes] = await Promise.all([
          fetch('/api/users', { headers: authJsonHeaders() }),
          fetch(`/api/cabinets/${cabinet.id}/members`, { headers: authJsonHeaders() }),
        ]);
        const usersData = await usersRes.json();
        const membersData = await membersRes.json();
        if (cancelled) return;
        if (!usersRes.ok) {
          throw new Error(usersData.error || 'Impossible de charger les utilisateurs');
        }
        setAllUsers(Array.isArray(usersData.users) ? usersData.users : []);
        applyMembersToState(membersData.members);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Impossible de charger les utilisateurs');
          setAllUsers([]);
          setSelectedIds(new Set());
          setRolesByUser({});
          setModulesByUser({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cabinet?.id]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter((u) => {
      const hay = [u.username, u.first_name, u.last_name, u.email, userRoleLabel(u)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [allUsers, search]);

  const toggleUserAccess = (userId) => {
    const id = Number(userId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setRolesByUser((r) => {
          const copy = { ...r };
          delete copy[id];
          return copy;
        });
        setModulesByUser((m) => {
          const copy = { ...m };
          delete copy[id];
          return copy;
        });
      } else {
        next.add(id);
        setRolesByUser((r) => ({ ...r, [id]: r[id] || 'User' }));
        setModulesByUser((m) => ({
          ...m,
          [id]: m[id]?.length ? m[id] : [...ALL_MODULE_IDS],
        }));
      }
      return next;
    });
  };

  const filteredIds = useMemo(
    () => filteredUsers.map((u) => Number(u.id)).filter((id) => Number.isFinite(id)),
    [filteredUsers]
  );

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const toggleSelectAllFiltered = () => {
    if (saving || loading || filteredIds.length === 0) return;

    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
      setRolesByUser((r) => {
        const copy = { ...r };
        filteredIds.forEach((id) => delete copy[id]);
        return copy;
      });
      setModulesByUser((m) => {
        const copy = { ...m };
        filteredIds.forEach((id) => delete copy[id]);
        return copy;
      });
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return next;
    });
    setRolesByUser((r) => {
      const copy = { ...r };
      filteredIds.forEach((id) => {
        if (!copy[id]) copy[id] = 'User';
      });
      return copy;
    });
    setModulesByUser((m) => {
      const copy = { ...m };
      filteredIds.forEach((id) => {
        if (!copy[id]?.length) copy[id] = [...ALL_MODULE_IDS];
      });
      return copy;
    });
  };

  const setUserRole = (userId, role) => {
    const id = Number(userId);
    const value = role === 'Admin' ? 'Admin' : 'User';
    setRolesByUser((prev) => ({ ...prev, [id]: value }));
    setSelectedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      setModulesByUser((m) => ({
        ...m,
        [id]: m[id]?.length ? m[id] : [...ALL_MODULE_IDS],
      }));
      return next;
    });
  };

  const toggleUserModule = (userId, moduleId) => {
    const id = Number(userId);
    setModulesByUser((prev) => {
      const current = normalizeModules(prev[id], { defaultAll: true });
      const has = current.includes(moduleId);
      const next = has
        ? current.filter((m) => m !== moduleId)
        : [...current, moduleId];
      return { ...prev, [id]: ALL_MODULE_IDS.filter((m) => next.includes(m)) };
    });
    setSelectedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      setRolesByUser((r) => ({ ...r, [id]: r[id] || 'User' }));
      return next;
    });
  };

  const handleSave = async () => {
    if (saving) return;
    if (selectedIds.size === 0) {
      setError('Sélectionnez au moins un utilisateur avec accès pour ce cabinet.');
      return;
    }

    const members = [...selectedIds].map((uid) => ({
      user_id: uid,
      member_role: rolesByUser[uid] === 'Admin' ? 'Admin' : 'User',
      allowed_modules: normalizeModules(modulesByUser[uid], { defaultAll: false }),
    }));

    if (!members.some((m) => m.member_role === 'Admin')) {
      setError(
        `Le cabinet « ${cabinet.name || cabinet.id} » doit avoir au moins un Admin.`
      );
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // N'enregistre QUE ce cabinet — les autres cabinets ne sont pas touchés
      const res = await fetch(`/api/cabinets/${cabinet.id}/members`, {
        method: 'PUT',
        headers: authJsonHeaders(),
        body: JSON.stringify({ members }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Enregistrement impossible');
      }

      setSuccess(`Points d’accès enregistrés pour « ${cabinet.name} ».`);
      applyMembersToState(data.members);
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const initials = (u) => {
    const name = userDisplayName(u);
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return String(name).slice(0, 2).toUpperCase() || '?';
  };

  return (
    <div className="pa-detail">
      <header className="pa-detail__hero">
        <div className="pa-detail__hero-glow" aria-hidden="true" />
        <div className="pa-detail__hero-text">
          <div className="pa-detail__eyebrow">Point d&apos;accès</div>
          <h1>{cabinet.name || 'Cabinet'}</h1>
          <p>
            Cochez les utilisateurs de <strong>ce cabinet</strong>, leur{' '}
            <strong>rôle</strong> (Admin / User) et les <strong>modules</strong>. Un même
            utilisateur coché aussi sur un autre cabinet aura accès aux deux.
          </p>
        </div>
        <div className="pa-detail__hero-actions">
          <button type="button" className="cabinets-btn cabinets-btn--ghost" onClick={onBack}>
            ← Retour aux cabinets
          </button>
          <button
            type="button"
            className="cabinets-btn cabinets-btn--primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </header>

      {error ? <div className="cabinets-alert">{error}</div> : null}
      {success ? <div className="cabinets-success">{success}</div> : null}

      <div className="pa-detail__toolbar">
        <label className="pa-detail__search">
          <span className="pa-detail__search-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
            </svg>
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un utilisateur…"
            aria-label="Recherche"
          />
        </label>
        <div className="pa-detail__stats">
          <span className="pa-detail__stat pa-detail__stat--access">
            <strong>{selectedIds.size}</strong> avec accès
          </span>
          <span className="pa-detail__stat">
            <strong>{filteredUsers.length}</strong> utilisateur
            {filteredUsers.length !== 1 ? 's' : ''}
          </span>
        </div>
        <label className="pa-detail__select-all">
          <input
            type="checkbox"
            checked={allFilteredSelected}
            disabled={saving || loading || filteredIds.length === 0}
            onChange={toggleSelectAllFiltered}
            aria-label="Sélectionner tous les utilisateurs"
          />
          <span>Sélectionner tous</span>
        </label>
      </div>

      {loading ? (
        <div className="cabinets-loading">Chargement des utilisateurs…</div>
      ) : filteredUsers.length === 0 ? (
        <div className="cabinets-empty">Aucun utilisateur trouvé.</div>
      ) : (
        <div className="pa-user-list" role="list">
          {filteredUsers.map((u) => {
            const id = Number(u.id);
            const checked = selectedIds.has(id);
            const role = rolesByUser[id] === 'Admin' ? 'Admin' : 'User';
            const mods = normalizeModules(modulesByUser[id], {
              defaultAll: false,
            });
            const sysRole = userRoleLabel(u);
            return (
              <article
                key={id}
                role="listitem"
                className={`pa-user-card${checked ? ' is-active' : ''}`}
              >
                <div className="pa-user-card__left">
                  <label className="pa-user-card__check">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={saving}
                      aria-label={`Accès ${cabinet.name} pour ${userDisplayName(u)}`}
                      onChange={() => toggleUserAccess(id)}
                    />
                  </label>
                  <div className={`pa-user-card__avatar${checked ? ' is-on' : ''}`} aria-hidden="true">
                    {initials(u)}
                  </div>
                  <div className="pa-user-card__identity">
                    <strong>{userDisplayName(u)}</strong>
                    <span className="pa-user-card__username">@{u.username || '—'}</span>
                    <span
                      className={`pa-user-card__sys${
                        sysRole === 'Admin système' ? ' pa-user-card__sys--admin' : ''
                      }`}
                    >
                      {sysRole}
                    </span>
                  </div>
                </div>

                <div className="pa-user-card__right">
                  <div className="pa-user-card__role">
                    <span className="pa-user-card__field-label">Rôle cabinet</span>
                    {checked ? (
                      <div className="pa-role-toggle" role="group" aria-label={`Rôle pour ${userDisplayName(u)}`}>
                        <button
                          type="button"
                          className={`pa-role-toggle__btn${role === 'User' ? ' is-on' : ''}`}
                          disabled={saving}
                          onClick={() => setUserRole(id, 'User')}
                        >
                          User
                        </button>
                        <button
                          type="button"
                          className={`pa-role-toggle__btn${role === 'Admin' ? ' is-on is-admin' : ''}`}
                          disabled={saving}
                          onClick={() => setUserRole(id, 'Admin')}
                        >
                          Admin
                        </button>
                      </div>
                    ) : (
                      <span className="pa-user-card__muted">—</span>
                    )}
                  </div>

                  <div className="pa-user-card__modules">
                    <span className="pa-user-card__field-label">Modules</span>
                    {checked ? (
                      <div
                        className="pa-modules"
                        role="group"
                        aria-label={`Modules pour ${userDisplayName(u)}`}
                      >
                        {APP_MODULES.map((mod) => (
                          <label
                            key={mod.id}
                            className={`pa-module-chip${mods.includes(mod.id) ? ' is-on' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={mods.includes(mod.id)}
                              disabled={saving}
                              onChange={() => toggleUserModule(id, mod.id)}
                            />
                            <span>{mod.label}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <span className="pa-user-card__muted">Activez l’accès pour choisir les modules</span>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminPointAccesPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { cabinetId } = useParams();
  const [cabinets, setCabinets] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canManage = isAdmin();

  const loadCabinets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/cabinets', { headers: authJsonHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Impossible de charger les cabinets');
      }
      const list = Array.isArray(data.cabinets) ? data.cabinets : [];
      setCabinets(list);

      const counts = {};
      await Promise.all(
        list.map(async (cab) => {
          try {
            const mRes = await fetch(`/api/cabinets/${cab.id}/members`, {
              headers: authJsonHeaders(),
            });
            const mData = await mRes.json();
            counts[cab.id] = Array.isArray(mData.members) ? mData.members.length : 0;
          } catch (_) {
            counts[cab.id] = 0;
          }
        })
      );
      setMemberCounts(counts);
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
      setCabinets([]);
      setMemberCounts({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canManage) loadCabinets();
  }, [canManage, loadCabinets]);

  const selectedCabinet = useMemo(() => {
    if (!cabinetId) return null;
    return cabinets.find((c) => String(c.id) === String(cabinetId)) || null;
  }, [cabinets, cabinetId]);

  if (!canManage) {
    return <Navigate to="/cabinets" replace />;
  }

  if (cabinetId && !loading && cabinets.length > 0 && !selectedCabinet) {
    return <Navigate to="/cabinets/acces" replace />;
  }

  return (
    <CabinetsAdminShell>
      {cabinetId && selectedCabinet ? (
        <CabinetMembersView
          cabinet={selectedCabinet}
          onBack={() => navigate('/cabinets/acces')}
          onSaved={() => loadCabinets()}
        />
      ) : cabinetId && loading ? (
        <div className="cabinets-loading">Chargement…</div>
      ) : (
        <CabinetsListView
          cabinets={cabinets}
          loading={loading}
          error={error}
          memberCounts={memberCounts}
          onManage={(cab) => navigate(`/cabinets/acces/${cab.id}`)}
        />
      )}
    </CabinetsAdminShell>
  );
}
